import { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  TableSortLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Info from './Info';
import LanguageSelector from './LanguageSelector';
import { useAdminLanguage } from './AdminI18n';
import useTableSort from './useTableSort';

function MagistratAdmin() {
  const { t } = useAdminLanguage();
  const [magistrats, setMagistrats] = useState([]);
  const [formData, setFormData] = useState({ 
    nom: '', 
    prenom: '', 
    titre: '', 
    parquet: '',
    genre: 'homme', // Valeur par défaut
    jugeInstruction: false // Nouvelle propriété pour Juge d'instruction
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [language, setLanguage] = useState('fr');

  useEffect(() => {
    fetchMagistrats();
  }, [language]);

  const fetchMagistrats = async () => {
    try {
      const response = await fetch(`/api/magistrats?lang=${language}`);
      const data = await response.json();
      setMagistrats(data);
      setLoading(false);
    } catch (err) {
      setError('Erreur lors du chargement des magistrats');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.nom.trim() || !formData.prenom.trim() || !formData.titre.trim() || !formData.parquet.trim() || !formData.genre) {
        setError('Tous les champs sont obligatoires');
        return;
      }

      setLoading(true);
      setError('');
      setSuccess('');

      const dataToSend = { ...formData, nom: formData.nom.toUpperCase(), lang: language };

      if (isEditing) {
        await fetch(`/api/magistrats/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dataToSend),
        });
        setSuccess('Magistrat mis à jour avec succès');
      } else {
        await fetch('/api/magistrats', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dataToSend),
        });
        setSuccess('Magistrat ajouté avec succès');
      }

      setFormData({ nom: '', prenom: '', titre: '', parquet: '', genre: 'homme', jugeInstruction: false });
      setIsEditing(false);
      setEditingId(null);
      fetchMagistrats();
    } catch (err) {
      setError('Erreur lors de l\'enregistrement du magistrat');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (magistrat) => {
    setIsEditing(true);
    setEditingId(magistrat.id);
    setFormData({
      nom: magistrat.nom,
      prenom: magistrat.prenom,
      titre: magistrat.titre,
      parquet: magistrat.parquet,
      genre: magistrat.genre || 'homme', // Valeur par défaut si le champ n'existe pas
      jugeInstruction: magistrat.jugeInstruction || false // Valeur par défaut si le champ n'existe pas
    });
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await fetch(`/api/magistrats/${id}?lang=${language}`, {
        method: 'DELETE',
      });
      setSuccess('Magistrat supprimé avec succès');
      fetchMagistrats();
    } catch (err) {
      setError('Erreur lors de la suppression du magistrat');
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => ([
    { id: 'nom', label: t('Nom'), sortable: true },
    { id: 'prenom', label: t('Prénom'), sortable: true },
    { id: 'titre', label: t('Titre'), sortable: true },
    { id: 'parquet', label: t('Parquet'), sortable: true },
    { id: 'genre', label: t('Genre'), sortable: true },
    { id: 'jugeInstruction', label: 'JI', sortable: true },
  ]), [t]);

  const getComparableValue = (magistrat, property) => {
    switch (property) {
      case 'genre':
        return magistrat.genre || '';
      case 'jugeInstruction':
        return magistrat.jugeInstruction ? 1 : 0;
      default:
        return magistrat[property]?.toString().toLowerCase() || '';
    }
  };

  const { order, orderBy, sortedRows, handleRequestSort } = useTableSort(magistrats, {
    defaultOrderBy: 'nom',
    getComparableValue,
  });

  const renderCellContent = (magistrat, columnId) => {
    switch (columnId) {
      case 'genre':
        return magistrat.genre === 'femme' ? t('Femme') : t('Homme');
      case 'jugeInstruction':
        return magistrat.jugeInstruction ? t('Oui') : t('Non');
      default:
        return magistrat[columnId];
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in timeout={500}>
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            color="primary"
            sx={{ fontWeight: 'bold', mb: 4 }}
          >
            {t('Gestion des Magistrats')}
          </Typography>

          <Info type="magistrat" />

          <LanguageSelector 
            language={language} 
            onLanguageChange={setLanguage} 
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t(error)}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t(success)}
            </Alert>
          )}

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {t(isEditing ? 'Modifier le magistrat' : 'Ajouter un magistrat')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="nom"
                  label={t('Nom')}
                  value={formData.nom}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  disabled={loading}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="prenom"
                  label={t('Prénom')}
                  value={formData.prenom}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  disabled={loading}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  name="titre"
                  label={t('Titre')}
                  value={formData.titre}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  disabled={loading}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  name="parquet"
                  label={t('Préfixe + Parquet (ex : près le Parquet de..)')}
                  value={formData.parquet}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  disabled={loading}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="genre-label">{t('Genre')}</InputLabel>
                  <Select
                    labelId="genre-label"
                    name="genre"
                    value={formData.genre}
                    onChange={handleInputChange}
                    label={t('Genre')}
                    disabled={loading}
                  >
                    <MenuItem value="homme">{t('Homme')}</MenuItem>
                    <MenuItem value="femme">{t('Femme')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="jugeInstruction"
                      checked={formData.jugeInstruction}
                      onChange={handleInputChange}
                      disabled={loading}
                    />
                  }
                  label={t("Juge d'instruction")}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={loading}
                  startIcon={loading && <CircularProgress size={20} />}
                >
                  {loading ? t('Enregistrement...') : t(isEditing ? 'Modifier' : 'Ajouter')}
                </Button>
                {isEditing && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setIsEditing(false);
                      setEditingId(null);
                      setFormData({ nom: '', prenom: '', titre: '', parquet: '', genre: 'homme', jugeInstruction: false });
                    }}
                    sx={{ ml: 2 }}
                  >
                    Annuler
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              {t('Liste des magistrats')}
            </Typography>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        sortDirection={orderBy === column.id ? order : false}
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={orderBy === column.id}
                            direction={orderBy === column.id ? order : 'asc'}
                            onClick={() => handleRequestSort(column.id)}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                    <TableCell>{t('Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRows.map((magistrat) => (
                    <TableRow key={magistrat.id}>
                      {columns.map((column) => (
                        <TableCell key={`${magistrat.id}-${column.id}`}>
                          {renderCellContent(magistrat, column.id)}
                        </TableCell>
                      ))}
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(magistrat)}
                            color="primary"
                            disabled={loading}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(magistrat.id)}
                            color="error"
                            disabled={loading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Fade>
    </Container>
  );
}

export default MagistratAdmin;